/*@flow*/
import type { Element } from 'react'; /** @jsx jsx */
import { jsx } from '@emotion/core';
import { useContext, useState } from 'react';
import panels from 'comps/Left2/panels';
import Context from './Context';
import commands from './commands';
import AsyncDropdown from 'comps/AsyncDropdown';
import { PANELS_AS_OPTIONS } from 'comps/District/defaultPanels';
const { FILTERS, ANALYSIS, LIST_VIEW, LAYERS } = panels;

const CheckBtn = ({ checked, onChange, className, ...rest }) => (
  <input
    {...rest}
    type="checkbox"
    value
    checked={checked}
    onChange={() => {}}
    className={className || ''}
    onClick={(e) => {
      onChange(e.target.checked);
    }}
  />
);

function Dropdown({
  value,
  onChange,
  defaultValues = [],
  isMulti = false,
}: {
  value: any,
  onChange: (any) => void,
  defaultValues: any[],
  isMulti?: boolean,
}) {
  return (
    <AsyncDropdown
      loadValues={() => Promise.resolve(defaultValues)}
      onChange={onChange}
      value={value}
      isMulti={isMulti}
    />
  );
}

function OpenPanelSelection({ value, onChange }) {
  const values = [FILTERS, ANALYSIS, LIST_VIEW, LAYERS];
  return (
    <div data-testid="OpenPanelSelection">
      <Dropdown defaultValues={values} onChange={onChange} value={value} />
    </div>
  );
}

function ToolbarSelection({ value, onChange }) {
  return (
    <div data-testid="ToolbarSelection">
      <Dropdown
        defaultValues={PANELS_AS_OPTIONS}
        onChange={onChange}
        value={value}
        isMulti
      />
    </div>
  );
}

export default function SidebarSetting({
  showImagesInListView,
  setShowImagesInListView,
  setSortByLastUpdated,
  sortByLastUpdated,
}): Element<string> {
  const ctx = useContext(Context);
  const { panelName, toolbarButtons } = ctx.state;
  const [customizeOpenPanel, setOpenPanel] = useState(false);
  const [customizeToolbar, setCustomizeToolbar] = useState(false);
  const cmd = commands(ctx);

  const onPanelChanged = (v) => cmd.set('panelName', v);
  const onToolbarButtonsChanged = (v) => cmd.set('toolbarButtons', v);

  return (
    <div data-testid="SidebarSetting">
      <h5 className="font-weight-bold">Sidebar settings</h5>
      <label
        data-testid="ShowPanelOption"
        className="d-flex align-items-center"
      >
        <span>Show a panel open</span>
        <CheckBtn
          data-testid="ShowPanelCheckButton"
          className="ml-auto"
          checked={customizeOpenPanel}
          onChange={setOpenPanel}
        />
      </label>
      {customizeOpenPanel && (
        <OpenPanelSelection value={panelName} onChange={onPanelChanged} />
      )}
      <label
        data-testid="CustomizeToolbarOption"
        className="d-flex align-items-center mt-2"
      >
        <span>Customize toolbar</span>
        <CheckBtn
          data-testid="CustomizeToolbarCheckButton"
          className="ml-auto"
          checked={customizeToolbar}
          onChange={setCustomizeToolbar}
        />
      </label>
      {customizeToolbar && (
        <ToolbarSelection
          value={toolbarButtons}
          onChange={onToolbarButtonsChanged}
        />
      )}

      <label
        data-testid="ShowImagesCheckmark"
        className="d-flex align-items-center mt-2"
      >
        <span>Show images in list view</span>
        <CheckBtn
          data-testid="ShowImagesCheckmarkButton"
          className="ml-auto"
          checked={showImagesInListView}
          onChange={setShowImagesInListView}
        />
      </label>

      <label
        data-testid="SortByLastUpdated"
        className="d-flex align-items-center mt-2"
      >
        <span>Sort by Last Updated</span>
        <CheckBtn
          data-testid="SortByLastUpdatedButton"
          className="ml-auto"
          checked={sortByLastUpdated}
          onChange={setSortByLastUpdated}
        />
      </label>
    </div>
  );
}
